<template>
  <section id="features" class="py-12 pt-24 px-4 sm:px-8 xl:px-12 bg-white relative">
    <div class="max-w-[1400px] mx-auto space-y-20 relative z-10">
      <!-- Feature 1: The Job Board preview -->
      <div class="flex flex-col md:flex-row items-center gap-12 lg:gap-16 group">
        <!-- Left: Text content -->
        <div class="flex-1 space-y-8 relative">
          <div class="flex flex-wrap gap-3">
            <span class="bg-[#e8f0fe] text-[#1a66e6] px-4 py-2 rounded-full text-sm font-medium">
              Private Network
            </span>
            <span class="bg-[#e8f0fe] text-[#1a66e6] px-4 py-2 rounded-full text-sm font-medium">
              Curated Roles
            </span>
          </div>

          <h3 class="text-4xl md:text-5xl font-medium text-gray-900 leading-tight z-10">
            A private job board, filtered for quality.
          </h3>
          <p class="text-lg text-gray-600 font-normal leading-relaxed z-10 max-w-lg">
            Skip the noise of public platforms. Get access to high-signal opportunities sourced
            directly from the Outtalent staff and community members.
          </p>

          <ul class="space-y-4 text-gray-700 text-lg pt-4">
            <li class="flex items-center gap-4">
              <span
                class="w-8 h-8 flex items-center justify-center bg-[#ccff00] rounded-full shrink-0"
              >
                <svg
                  class="w-5 h-5 text-[#1a66e6]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 13l4 4L19 7"
                  ></path>
                </svg>
              </span>
              <span class="font-medium">Direct Hiring Manager Access</span>
            </li>
            <li class="flex items-center gap-4">
              <span
                class="w-8 h-8 flex items-center justify-center bg-[#ccff00] rounded-full shrink-0"
              >
                <svg
                  class="w-5 h-5 text-[#1a66e6]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 13l4 4L19 7"
                  ></path>
                </svg>
              </span>
              <span class="font-medium">Filter by Location and Experience Level</span>
            </li>
          </ul>
        </div>

        <!-- Right: Realistic Job Board Preview -->
        <div
          class="w-full md:w-[50%] bg-[#bffe78] rounded-[2.5rem] p-6 lg:p-10 relative overflow-hidden flex items-center justify-center"
        >
          <div
            class="w-full bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden transform group-hover:-translate-y-2 transition-transform duration-500"
          >
            <!-- Toolbar -->
            <div class="border-b border-gray-100 p-4 flex gap-3">
              <div
                class="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 flex items-center gap-2"
              >
                <svg
                  class="w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  ></path>
                </svg>
                <span class="text-sm text-gray-400">Search "Senior Backend Engineer"</span>
              </div>
              <div
                class="bg-blue-50 text-[#1a66e6] border border-blue-100 rounded-lg px-4 py-2 text-sm font-medium flex items-center gap-2 cursor-pointer"
              >
                Filter
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  ></path>
                </svg>
              </div>
            </div>

            <!-- Job List -->
            <div class="divide-y divide-gray-100">
              <!-- Job 1 -->
              <div class="p-5 hover:bg-gray-50 flex justify-between items-start">
                <div class="flex gap-4">
                  <div
                    class="w-10 h-10 rounded border border-gray-100 flex items-center justify-center bg-white shadow-sm shrink-0"
                  >
                    <img
                      src="https://logo.clearbit.com/netflix.com"
                      onerror="this.src='data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgdmlld0JveD0iMCAwIDQwIDQwIj48cmVjdCB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIGZpbGw9IiNlNWU1ZTUiLz48L3N2Zz4='"
                      class="h-6 w-6 object-contain"
                    />
                  </div>
                  <div>
                    <h4 class="text-base font-semibold text-gray-900">
                      Staff Software Engineer, Core Platform
                    </h4>
                    <div class="flex items-center gap-2 text-sm text-gray-500 mt-1">
                      <span class="font-medium text-gray-700">Google</span>
                      <span>•</span>
                      <span>London, United Kingdom</span>
                    </div>
                    <div class="flex gap-2 mt-3">
                      <span
                        class="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-medium border border-gray-200"
                        >Experienced</span
                      >
                    </div>
                  </div>
                </div>
                <div class="text-right flex flex-col items-end gap-3">
                  <span class="text-xs font-medium text-gray-400">2h ago</span>
                </div>
              </div>

              <!-- Job 2 -->
              <div class="p-5 hover:bg-gray-50 flex justify-between items-start">
                <div class="flex gap-4">
                  <div
                    class="w-10 h-10 rounded border border-gray-100 flex items-center justify-center bg-white shadow-sm shrink-0"
                  >
                    <img
                      src="https://logo.clearbit.com/spotify.com"
                      onerror="this.src='data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgdmlld0JveD0iMCAwIDQwIDQwIj48cmVjdCB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIGZpbGw9IiNlNWU1ZTUiLz48L3N2Zz4='"
                      class="h-6 w-6 object-contain"
                    />
                  </div>
                  <div>
                    <h4 class="text-base font-semibold text-gray-900">
                      Backend Engineer (Platform)
                    </h4>
                    <div class="flex items-center gap-2 text-sm text-gray-500 mt-1">
                      <span class="font-medium text-gray-700">Meta</span>
                      <span>•</span>
                      <span>Zurich, Switzerland</span>
                    </div>
                    <div class="flex gap-2 mt-3">
                      <span
                        class="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-medium border border-gray-200"
                        >Mid-level</span
                      >
                    </div>
                  </div>
                </div>
                <div class="text-right flex flex-col items-end gap-3">
                  <span class="text-xs font-medium text-gray-400">1d ago</span>
                </div>
              </div>

              <!-- Job 3 -->
              <div class="p-5 hover:bg-gray-50 flex justify-between items-start">
                <div class="flex gap-4">
                  <div
                    class="w-10 h-10 rounded border border-gray-100 flex items-center justify-center bg-white shadow-sm shrink-0"
                  >
                    <img
                      src="https://logo.clearbit.com/spotify.com"
                      onerror="this.src='data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgdmlld0JveD0iMCAwIDQwIDQwIj48cmVjdCB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIGZpbGw9IiNlNWU1ZTUiLz48L3N2Zz4='"
                      class="h-6 w-6 object-contain"
                    />
                  </div>
                  <div>
                    <h4 class="text-base font-semibold text-gray-900">Software Engineer 5</h4>
                    <div class="flex items-center gap-2 text-sm text-gray-500 mt-1">
                      <span class="font-medium text-gray-700">Netflix</span>
                      <span>•</span>
                      <span>Amsterdam, Netherlands</span>
                    </div>
                    <div class="flex gap-2 mt-3">
                      <span
                        class="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-medium border border-gray-200"
                        >Experienced</span
                      >
                    </div>
                  </div>
                </div>
                <div class="text-right flex flex-col items-end gap-3">
                  <span class="text-xs font-medium text-gray-400">1d ago</span>
                  <span
                    class="bg-blue-50 text-blue-600 border border-blue-100 px-3 py-1 rounded-full text-xs font-semibold shadow-sm"
                    >Remote</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Feature 2: Table / Details Tracker Preview -->
      <div class="flex flex-col md:flex-row-reverse items-center gap-12 lg:gap-16 group">
        <!-- Text content -->
        <div class="flex-1 space-y-8 relative">
          <div class="flex flex-wrap gap-3">
            <span class="bg-[#e8f0fe] text-[#1a66e6] px-4 py-2 rounded-full text-sm font-medium">
              Application Pipeline
            </span>
            <span class="bg-[#e8f0fe] text-[#1a66e6] px-4 py-2 rounded-full text-sm font-medium">
              Real-time Tracking
            </span>
          </div>

          <h3 class="text-4xl md:text-5xl font-medium text-gray-900 leading-tight">
            Stop guessing your interview status.
          </h3>
          <p class="text-lg text-gray-600 font-normal leading-relaxed max-w-lg">
            Maintain complete visibility over your progress. Move applications seamlessly across
            customized statuses and columns, right to the final offer.
          </p>

          <ul class="space-y-4 text-gray-700 text-lg pt-4">
            <li class="flex items-center gap-4">
              <span
                class="w-8 h-8 flex items-center justify-center bg-[#1a66e6] rounded-full shrink-0"
              >
                <svg
                  class="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 13l4 4L19 7"
                  ></path>
                </svg>
              </span>
              <span class="font-medium text-gray-900">Intelligent Match Assessment</span>
            </li>
            <li class="flex items-center gap-4">
              <span
                class="w-8 h-8 flex items-center justify-center bg-[#1a66e6] rounded-full shrink-0"
              >
                <svg
                  class="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 13l4 4L19 7"
                  ></path>
                </svg>
              </span>
              <span class="font-medium text-gray-900">Dynamic Application Tracking Dashboards</span>
            </li>
          </ul>
        </div>

        <!-- Right: Realistic Details/Table Pipeline Preview -->
        <div
          class="w-full md:w-[50%] bg-[#1a66e6] rounded-[2.5rem] p-6 lg:p-10 relative overflow-hidden flex items-center justify-center"
        >
          <div
            class="w-full bg-white rounded-2xl shadow-2xl overflow-hidden transform group-hover:scale-[1.02] transition-transform duration-500 flex flex-col text-[10px] sm:text-xs text-left"
          >
            <!-- Job Info -->
            <div class="p-3 sm:p-5 flex flex-col gap-3">
              <div class="flex justify-between items-start">
                <div>
                  <h3 class="font-bold text-gray-900 text-sm sm:text-base">
                    Senior Full-Stack Developer
                  </h3>
                  <p class="font-semibold text-gray-800 text-xs sm:text-sm">Stripe</p>
                  <div class="flex items-center gap-1 mt-1 text-gray-500 text-[10px] sm:text-xs">
                    <svg
                      class="w-3 h-3 sm:w-4 sm:h-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      ></path>
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      ></path>
                    </svg>
                    Dublin, Ireland
                  </div>
                </div>
                <div class="flex flex-col items-end gap-2">
                  <div
                    class="h-8 w-8 sm:h-12 sm:w-12 rounded-lg bg-gray-100 flex items-center justify-center"
                  ></div>
                </div>
              </div>

              <div
                class="flex flex-wrap justify-between items-center sm:mt-2 text-[9px] sm:text-xs gap-2 border-b border-gray-100 pb-3"
              >
                <span
                  class="bg-[#e8f5e9] text-[#2e7d32] border border-[#a5d6a7] px-2 py-1 rounded-full font-medium flex items-center gap-1"
                >
                  <svg
                    class="w-3 h-3 sm:w-4 sm:h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path>
                  </svg>
                  Referral request sent
                </span>
                <div
                  class="flex flex-col sm:flex-row items-end sm:items-center gap-1 sm:gap-4 ml-auto"
                >
                  <div class="hidden md:flex items-center gap-2 text-gray-600">
                    <span
                      class="flex items-center gap-1 px-2 py-1 border border-gray-200 rounded text-gray-700 bg-white cursor-pointer"
                      ><svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        ></path>
                      </svg>
                      View on TrackTalent</span
                    >
                    <span
                      class="flex items-center gap-1 px-2 py-1 border border-gray-200 rounded text-gray-700 bg-white cursor-pointer"
                      ><svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        ></path>
                      </svg>
                      View on company website</span
                    >
                  </div>
                </div>
              </div>

              <!-- Dropdowns Row -->
              <div class="grid grid-cols-2 gap-3 sm:gap-6 mt-1 sm:mt-2 text-[10px] sm:text-xs">
                <div class="space-y-1 sm:space-y-2">
                  <label
                    class="font-bold text-gray-700 flex items-center gap-1 text-[9px] sm:text-xs"
                  >
                    <svg
                      class="w-3 h-3 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 13l4 4L19 7"
                      ></path>
                    </svg>
                    Interview Stage
                  </label>
                  <div
                    class="border border-gray-200 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 flex justify-between items-center bg-gray-50 text-gray-600"
                  >
                    <span
                      class="bg-lime-200 px-2 py-0.5 rounded-full text-green-700 border border-lime-400 font-medium"
                      >Onsite</span
                    >
                    <svg
                      class="w-3 h-3 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 9l-7 7-7-7"
                      ></path>
                    </svg>
                  </div>
                </div>
                <div class="space-y-1 sm:space-y-2">
                  <label
                    class="font-bold text-gray-700 flex items-center gap-1 text-[9px] sm:text-xs"
                  >
                    <svg
                      class="w-3 h-3 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      ></path>
                    </svg>
                    Application Status
                  </label>
                  <div
                    class="border border-gray-200 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 flex justify-between items-center bg-white text-gray-700"
                  >
                    <span
                      class="bg-blue-100 px-2 py-0.5 rounded-full text-blue-700 font-medium border border-blue-200"
                      >Ongoing</span
                    >
                    <svg
                      class="w-3 h-3 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 9l-7 7-7-7"
                      ></path>
                    </svg>
                  </div>
                </div>
                <div class="space-y-1 sm:space-y-2">
                  <label
                    class="font-bold text-gray-700 flex items-center gap-1 text-[9px] sm:text-xs"
                  >
                    <svg
                      class="w-3 h-3 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      ></path>
                    </svg>
                    Referral Type
                  </label>
                  <div
                    class="border border-gray-200 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 flex justify-between items-center bg-white text-gray-600"
                  >
                    <span
                      class="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium border border-indigo-200"
                      >I will rely on Outtalent referral</span
                    >
                    <svg
                      class="w-3 h-3 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 9l-7 7-7-7"
                      ></path>
                    </svg>
                  </div>
                </div>
                <div class="space-y-1 sm:space-y-2">
                  <label
                    class="font-bold text-gray-700 flex items-center gap-1 text-[9px] sm:text-xs"
                  >
                    <svg
                      class="w-3 h-3 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 13l4 4L19 7"
                      ></path>
                    </svg>
                    Referral Status
                  </label>
                  <div
                    class="border border-gray-200 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 flex justify-between items-center bg-white text-gray-600"
                  >
                    <span
                      class="bg-[#ccff00] text-green-900 border border-[#b2e600] px-2 py-0.5 rounded-full font-medium"
                      >Referral submitted by champion</span
                    >
                    <svg
                      class="w-3 h-3 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 9l-7 7-7-7"
                      ></path>
                    </svg>
                  </div>
                </div>
              </div>

              <!-- Match Assessment Card -->
              <div
                class="mt-2 sm:mt-4 border border-gray-200 rounded-xl overflow-hidden text-[10px] sm:text-xs shadow-sm"
              >
                <div
                  class="bg-gray-50 px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-200 flex justify-between items-center"
                >
                  <div
                    class="font-bold text-gray-900 flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
                  >
                    <svg
                      class="w-3 h-3 sm:w-4 sm:h-4 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      ></path>
                    </svg>
                    Match Assessment
                  </div>
                  <div class="text-gray-500 font-medium flex items-center gap-1 sm:gap-2">
                    Match Score:
                    <span
                      class="bg-[#e8f5e9] text-[#2e7d32] border border-[#a5d6a7] font-bold px-2 py-0.5 rounded-full text-[10px] sm:text-xs"
                      >8.5/10</span
                    >
                  </div>
                </div>
                <div class="p-3 sm:p-4 bg-white space-y-3 sm:space-y-4">
                  <div
                    class="bg-green-50 border border-green-200 text-green-800 px-3 py-2 rounded-lg text-[10px] sm:text-xs flex items-center gap-2 font-medium"
                  >
                    <svg
                      class="w-3 h-3 sm:w-4 sm:h-4 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 13l4 4L19 7"
                      ></path>
                    </svg>
                    High Match: Our system will process your referral request as soon as possible.
                  </div>

                  <p class="text-gray-700 leading-relaxed text-[10px] sm:text-xs">
                    Strong alignments with minimum requirements for a generalist software engineer
                    who can work across the stack
                  </p>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                    <div class="border border-green-200 bg-green-50/50 rounded-lg p-3 sm:p-4">
                      <h5
                        class="font-bold text-green-900 flex items-center gap-1 sm:gap-2 mb-2 text-[10px] sm:text-xs"
                      >
                        <svg
                          class="w-3 h-3 sm:w-4 sm:h-4 text-green-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          ></path>
                        </svg>
                        Key Alignments
                      </h5>
                      <ul
                        class="list-disc pl-4 text-green-800 space-y-1 sm:space-y-1.5 text-[9px] sm:text-[10px]"
                      >
                        <li>Resume meets "5+ years of experience" requirement</li>
                        <li>
                          Experience with across-the-stack work and user-facing + data/query work
                        </li>
                        <li>Extensive cloud platform experience and serverless components</li>
                      </ul>
                    </div>
                    <div class="border border-amber-200 bg-amber-50/50 rounded-lg p-3 sm:p-4">
                      <h5
                        class="font-bold text-amber-900 flex items-center gap-1 sm:gap-2 mb-2 text-[10px] sm:text-xs"
                      >
                        <svg
                          class="w-3 h-3 sm:w-4 sm:h-4 text-amber-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                          ></path>
                        </svg>
                        Potential Gaps
                      </h5>
                      <ul
                        class="list-disc pl-4 text-amber-800 space-y-1 sm:space-y-1.5 text-[9px] sm:text-[10px]"
                      >
                        <li>Preferred experience building financial systems not evidenced</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts"></script>
